import React, { useState } from "react";
import { OverlayTrigger, Popover, Button } from "react-bootstrap";
import {
  updateAppointmentStatusByPatientId,
  getAppointmentById,
} from "../../api";
import {
  useAppointmentStatuses,
  isTransitionAllowed,
} from "../../hooks/useAppointmentStatuses";
import "./UpdateStatus.css";
import CompleteAppointmentModal from "../FullCalendar/CompleteAppointment"; // Import the modal
import { useNavigate } from "react-router-dom"; // Import useNavigate

export function StatusPopover({
  appointmentId,
  currentStatus,
  patientId,
  practiceNumber,
  onUpdate,
}) {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [loading, setLoading] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false); // ✅ Controls modal visibility
  const [appointment, setAppointment] = useState(null);
  const navigate = useNavigate(); // Initialize useNavigate

  // The practice's own statuses — this list used to be hardcoded here and in
  // three other files, which had already drifted apart.
  const {
    statuses,
    resolve,
    typeOf,
    behaviourOf,
  } = useAppointmentStatuses(practiceNumber);

  const currentType = typeOf(selectedStatus, practiceNumber);
  const behaviourForType = (type) =>
    behaviourOf(
      statuses.find((s) => s.type === type)?.key || selectedStatus,
      practiceNumber
    );

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // ✅ Handle status change logic
  const handleStatusChange = async (newStatus) => {
    if (newStatus === selectedStatus) return;
    setLoading(true);
    try {
      const response = await getAppointmentById(appointmentId);
      setAppointment(response);

      // ✅ If status is "Completed", trigger FollowUpSession logic
      if (typeOf(newStatus, practiceNumber) === "Completed" && onUpdate) {
        const wasSaved = await onUpdate(newStatus); // This calls handleExternalCompletion from the ref
        if (!wasSaved) {
          navigate(`/patients/appointment/${patientId}/${appointmentId}`);
          console.log("❌ Cannot complete: No template selected.");
          setLoading(false);
          return;
        }

        // ✅ Show modal ONLY if session saved
        setShowModal(true);
        setShowPopover(false);
        setLoading(false);
        return;
      }

      // ✅ Regular status update
      await updateAppointmentStatusByPatientId(appointmentId, newStatus);
      setSelectedStatus(newStatus);
      onUpdate(newStatus, false); // Fallback
      setShowPopover(false);
    } catch (error) {
      console.error("❌ Failed to update status:", error);
    }
    setLoading(false);
  };

  const handleCompleteAppointment = async () => {
    try {
      // No status update here — we defer this to the invoice modal completion

      setShowModal(false); // ✅ Close modal only after user completes everything
    } catch (error) {
      console.error("❌ Failed during complete appointment logic:", error);
    }
  };

  // ✅ Popover component (No header & no lines)
 const statusPopover = (
  <Popover id={`status-popover-${appointmentId}`} className="custom-popover">
    <Popover.Body>
      {statuses.map(({ key, label, icon, color, type }) => {
        const isCurrent = selectedStatus === key;

        // Transition rules live in one place now — see isTransitionAllowed.
        const isDisabled =
          !isCurrent && !isTransitionAllowed(currentType, type, behaviourForType);

        return (
          <div
            key={key}
            className={`d-flex align-items-center p-2 status-item ${
              isCurrent ? "active" : ""
            } ${isDisabled && !isCurrent ? "text-muted disabled-status" : ""}`}
            onClick={() => {
              if (isDisabled && !isCurrent) return; // prevent update
              handleStatusChange(key);
            }}
            style={{
              cursor: isDisabled && !isCurrent ? "not-allowed" : "pointer",
              opacity: isDisabled && !isCurrent ? 0.5 : 1,
            }}
          >
            <i
              className={`fa-solid ${icon} me-2`}
              style={{ color }}
            ></i>
            <span>{label}</span>
          </div>
        );
      })}
    </Popover.Body>
  </Popover>
);



  return (
    <>
      {/* Status Dropdown */}
      <OverlayTrigger
        show={showPopover}
        trigger="click"
        placement="bottom"
        overlay={statusPopover}
        rootClose
        onToggle={(isOpen) => setShowPopover(isOpen)}
      >
        <Button
          variant=""
          className="btn-bigger"
          onClick={() => setShowPopover(!showPopover)}
        >
          {/* resolve() always returns something renderable, so an unknown or
              archived status shows its own icon rather than generic dots. */}
          <i
            className={`fa-solid ${resolve(selectedStatus, practiceNumber).icon}`}
            style={{ color: resolve(selectedStatus, practiceNumber).color }}
            title={resolve(selectedStatus, practiceNumber).label}
          ></i>
        </Button>
      </OverlayTrigger>

      {/* ✅ Modal Opens When "Completed" is Selected */}
      {showModal && (
        <CompleteAppointmentModal
          appointment={appointment}
          onHide={handleCloseModal}
          show={showModal}
          onComplete={handleCompleteAppointment} // Pass the function to handle completion
        />
      )}
    </>
  );
}
